import { useCollection } from "@/features/collections/useCollection";
import {
  CollectionPagination,
  CollectionToolbar,
} from "@/features/collections/CollectionControls";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { Camera, Check, X } from "lucide-react";
import type {
  CoachingClip,
  CoachingNote,
  Player,
  PlayerGoal,
  PlayerPhotoRequest,
} from "@/lib/types";
import { errorMessage } from "@/lib/api";
import { useMe } from "@/features/auth/useMe";
import {
  usePlayerPhotoRequests,
  useRoster,
  useRosterMutations,
} from "./hooks/useRoster";
import {
  computeRosterKpis,
  filterRoster,
  formatNotesPerPlayer,
  type RosterStatusFilter,
} from "./rosterMetrics";
import { PlayerModal } from "./components/PlayerModal";
import { LinkAccountModal } from "./components/LinkAccountModal";
import { CoachModal } from "./components/CoachModal";
import { RosterImportModal } from "./components/RosterImportModal";
import { PageHeader } from "./components/PageHeader";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { TkticSelect } from "@/components/ui/TkticSelect";
import { useToast } from "@/components/ui/toast";
import { useCoachClipsList } from "./hooks/useCoachClipsList";
import { useCoachGoalsList } from "./hooks/useCoachGoalsList";
import { useCoachNotesList } from "./hooks/useCoachNotesList";
import { PLAYER_POSITION_OPTIONS } from "./playerPositions";
import { PlayerAvatar } from "@/features/player-photos/PlayerAvatar";
import { usePlayerPhoto } from "@/features/player-photos/usePlayerPhoto";

/**
 * Coach > Players list page.
 *
 * Phase 2 shifts the old Roster page into a Player Feedback Home:
 * the dominant surface is now "what should I do for this player?",
 * while roster administration stays available through focused
 * modal/actions instead of occupying the default page.
 *
 * Layout (restored from the legacy two-pane design):
 *   - Header — descriptive title + Add player modal entry point.
 *   - Two-pane body — player queue on the left, selected player home
 *     on the right. The full `PlayerModal` is still used for detailed
 *     edits where the active toggle and longer fields matter.
 *
 * KPIs reflect the FULL roster (not the filtered view) — legacy
 * behavior. The notes-per-player KPI needs every team note, so
 * this page also reads `getCoachingNotes()` (unfiltered).
 */

export type RosterPageProps = {
  canUseFeedbackSurfaces: boolean;
  /** Opens the simplified feedback capture flow for a player. */
  onGiveFeedback: (playerId?: string) => void;
  /** Opens the goal composer (create mode) preselected to this
   * player. Undefined ⇒ the row's goal button is hidden. Owned by
   * `CoachShell` so the goal + development modals stay
   * one-at-a-time. */
  onCreatePlayerGoal?: (playerId: string) => void;
  /** Opens one existing goal directly from the selected player's goal list. */
  onOpenPlayerGoal?: (goal: PlayerGoal) => void;
  /** Opens the Player Development modal for a player. Owned by
   * `CoachShell` (same one-modal-at-a-time reason). */
  onOpenDevelopment: (playerId: string, playerName: string) => void;
};

const STATUS_FILTERS: ReadonlyArray<{
  value: RosterStatusFilter;
  label: string;
}> = [
  { value: "all", label: "All" },
  { value: "active", label: "Active" },
  { value: "inactive", label: "Inactive" },
];

type PlayerAttentionFilter =
  | "all"
  | "needs_follow_up"
  | "need_help"
  | "no_recent_feedback"
  | "no_family_link"
  | "goals_stale";

type PlayerAccountLinkFilter = "all" | "linked" | "not_linked";

type PlayerArrivalContext =
  | "need_help"
  | "tried_this"
  | "no_recent_feedback"
  | "unreviewed_feedback"
  | "stale_goal"
  | "missing_recap";

const ATTENTION_FILTERS: ReadonlyArray<{
  value: PlayerAttentionFilter;
  label: string;
}> = [
  { value: "all", label: "All" },
  { value: "needs_follow_up", label: "Needs follow-up" },
  { value: "need_help", label: "Need help" },
  { value: "no_recent_feedback", label: "No recent feedback" },
  { value: "no_family_link", label: "No family link" },
  { value: "goals_stale", label: "Goal follow-up" },
];

const ACCOUNT_LINK_FILTERS: ReadonlyArray<{
  value: PlayerAccountLinkFilter;
  label: string;
}> = [
  { value: "all", label: "All" },
  { value: "linked", label: "Linked" },
  { value: "not_linked", label: "No link" },
];

export function RosterPage({
  canUseFeedbackSurfaces,
  onGiveFeedback,
  onCreatePlayerGoal,
  onOpenPlayerGoal,
  onOpenDevelopment,
}: RosterPageProps) {
  const navigate = useNavigate();
  const routeSearch = useSearch({ from: "/coach" });
  const toast = useToast();
  const me = useMe().data;
  // CSV roster import is a roster mutation — the backend gates both
  // import endpoints on the `roster:manage` capability (coach,
  // team_admin, global_admin; NOT assistant_coach). Gate the launch
  // button on the same capability so a coach sees it but an
  // assistant coach — who has read-only roster access — does not.
  const isGlobalAdmin = !!me?.user?.is_global_admin;
  const canManageRoster =
    isGlobalAdmin ||
    !!me?.active_scope?.capabilities?.includes("roster:manage");
  const rosterQuery = useRoster();
  const photoRequestsQuery = usePlayerPhotoRequests(canManageRoster);
  const mutations = useRosterMutations();

  const notesQuery = useCoachNotesList({ enabled: canUseFeedbackSurfaces });
  const clipsQuery = useCoachClipsList({ enabled: canUseFeedbackSurfaces });
  const goalsQuery = useCoachGoalsList(undefined, {
    enabled: canUseFeedbackSurfaces,
  });

  const players = useMemo(
    () => rosterQuery.data?.players ?? [],
    [rosterQuery.data],
  );
  const notes = useMemo(() => notesQuery.data?.notes ?? [], [notesQuery.data]);
  const clips = useMemo(() => clipsQuery.data?.clips ?? [], [clipsQuery.data]);
  const goals = useMemo(() => goalsQuery.data?.goals ?? [], [goalsQuery.data]);
  const kpis = useMemo(
    () => computeRosterKpis(players, notes),
    [players, notes],
  );

  // ---- Search + status filter ----
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<RosterStatusFilter>("all");
  const [accountLinkFilter, setAccountLinkFilter] =
    useState<PlayerAccountLinkFilter>("all");
  const [attentionFilter, setAttentionFilter] =
    useState<PlayerAttentionFilter>("all");
  const [selectedPlayerId, setSelectedPlayerId] = useState<string | null>(null);
  const [revealPlayerId, setRevealPlayerId] = useState<string | null>(null);
  useEffect(() => {
    if (routeSearch.player_id) {
      setSelectedPlayerId(routeSearch.player_id);
    }
  }, [routeSearch.player_id]);
  const matchingPlayers = useMemo(
    () =>
      filterPlayerFeedbackHome({
        players: filterRoster(players, search, statusFilter).filter((player) => {
          if (accountLinkFilter === "all") return true;
          const linked = (player.links ?? []).length > 0;
          return accountLinkFilter === "linked" ? linked : !linked;
        }),
        notes,
        clips,
        goals,
        filter: attentionFilter,
      }),
    [
      accountLinkFilter,
      attentionFilter,
      clips,
      goals,
      notes,
      players,
      search,
      statusFilter,
    ],
  );
  const collection = useCollection(players, {
    scope: "roster",
    text: (p) => p.display_name,
    predicate: (p) => matchingPlayers.some((m) => m.id === p.id),
  });
  const filtered = collection.items;
  useEffect(() => {
    collection.setPage(1);
  }, [search, statusFilter, accountLinkFilter, attentionFilter]);
  useEffect(() => {
    if (players.length <= 5) {
      setSearch("");
      setStatusFilter("all");
      setAccountLinkFilter("all");
      setAttentionFilter("all");
    }
  }, [players.length]);
  useEffect(() => {
    setSearch("");
    setStatusFilter("all");
    setAccountLinkFilter("all");
    setAttentionFilter("all");
  }, [me?.active_scope?.team?.id, me?.active_scope?.season?.id]);
  useEffect(() => {
    if (revealPlayerId && players.some((p) => p.id === revealPlayerId)) {
      collection.reveal((p) => p.id === revealPlayerId);
      setRevealPlayerId(null);
    }
  }, [revealPlayerId, players]);
  const selectedPlayer = useMemo(() => {
    return (
      players.find((p) => p.id === selectedPlayerId) ?? filtered[0] ?? null
    );
  }, [filtered, players, selectedPlayerId]);
  const selectPlayer = (playerId: string) => {
    setSelectedPlayerId(playerId);
    void navigate({
      to: "/coach",
      search: { tab: "roster", player_id: playerId },
    });
  };

  // ---- Modal state ----
  // `addPlayerOpen`: the focused add-player modal. `editModal`: the
  // player being edited (full PlayerModal). `linkModal`: the player
  // being linked from the selected-player Accounts card.
  const [addPlayerOpen, setAddPlayerOpen] = useState(false);
  const [editModal, setEditModal] = useState<Player | null>(null);
  const [linkModal, setLinkModal] = useState<Player | null>(null);
  const [importOpen, setImportOpen] = useState(false);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<Player | null>(null);

  const handleAddObservation = (playerId: string) => {
    // The M4→M6 carry-in: route to Coach Review's tactical-board
    // surface with the player preselected. Coach Review reads
    // `?source` + `?player_id` from the search params.
    navigate({
      to: "/coach/review",
      search: { source: "tactical_board", player_id: playerId },
    });
  };

  const handleAddNoteClip = (playerId: string) => {
    // The richer-feedback path: route to Coach Review's video surface
    // (source=video → a video note / clip) with the player preselected.
    // The coach picks the match there, same as the observation route.
    navigate({
      to: "/coach/review",
      search: { source: "video", player_id: playerId },
    });
  };

  const confirmDeletePlayer = async () => {
    if (!confirmDelete) return;
    const player = confirmDelete;
    setConfirmDelete(null);
    setDeleting(player.id);
    try {
      await mutations.deletePlayer(player.id);
      toast.success("Player deleted");
    } catch (err) {
      toast.error("Couldn't delete player", { description: errorMessage(err) });
    } finally {
      setDeleting(null);
    }
  };

  return (
    <div className="flex flex-col gap-4" data-testid="roster-page">
      {confirmDelete ? (
        <ConfirmDialog
          title="Delete player?"
          message={`Delete ${confirmDelete.display_name}? This removes the player and their account links.`}
          confirmLabel="Delete"
          danger
          onConfirm={confirmDeletePlayer}
          onClose={() => setConfirmDelete(null)}
          data-testid="roster-delete-confirm"
        />
      ) : null}
      <PageHeader
        eyebrow="The squad"
        title="Players"
        description={
          canUseFeedbackSurfaces
            ? "Open a player, understand what they need next, then create feedback or follow up with the right context."
            : "Manage roster details, player photos, and family/player account links."
        }
        actions={
          <>
            {canUseFeedbackSurfaces ? (
              <Button
                type="button"
                onClick={() => onGiveFeedback(selectedPlayer?.id)}
                data-testid="players-give-feedback"
                size="sm"
              >
                Give feedback
              </Button>
            ) : null}
            {canManageRoster ? (
              <Button
                type="button"
                onClick={() => setAddPlayerOpen(true)}
                data-testid="roster-add-player-open"
                variant="outline"
                size="sm"
              >
                Add player
              </Button>
            ) : null}
            {canManageRoster ? (
              <Button
                variant="ghost"
                onClick={() => setImportOpen(true)}
                data-testid="roster-import-open"
              >
                Import roster
              </Button>
            ) : null}
          </>
        }
      />

      {canManageRoster ? (
        <PhotoRequestsPanel
          requests={photoRequestsQuery.data?.requests ?? []}
          loading={photoRequestsQuery.isLoading}
          mutations={mutations}
        />
      ) : null}

      {/* KPI tiles — reflect the full roster, not the filtered view. */}
      <div
        className="grid grid-cols-2 gap-3 lg:grid-cols-4"
        data-testid="roster-kpis"
      >
        <KpiTile label="Active Players" value={String(kpis.activePlayers)} />
        <KpiTile label="Linked Accounts" value={String(kpis.linkedAccounts)} />
        <KpiTile label="Without Family Link" value={String(kpis.withoutLink)} />
        <KpiTile
          label="Avg. Notes / Player"
          value={formatNotesPerPlayer(kpis.notesPerPlayer)}
        />
      </div>

      {rosterQuery.isLoading ? (
        <p
          className="text-ink-muted text-sm font-mono px-3 py-6"
          data-testid="roster-loading"
        >
          Loading players...
        </p>
      ) : players.length === 0 ? (
        <p
          className="text-ink-muted text-sm px-3 py-6"
          data-testid="roster-empty"
        >
          No players yet. Add the first player to start the roster.
        </p>
      ) : (
        <div className="coach-roster">
          <section
            className="coach-player-queue"
            aria-label="Players"
            data-testid="roster-card-list"
          >
            <header className="border-b border-rule px-3 py-2.5">
              <h2 className="text-sm font-display font-semibold uppercase tracking-wider text-ink-muted">
                Player queue
              </h2>
            </header>
            {/* Controls use the complete authorized roster count. */}
            {collection.controls ? (
              <div
                className="player-queue-filters"
                data-testid="player-queue-filters"
              >
                <label className="player-queue-search">
                  <span>Quick search</span>
                  <input
                    type="search"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Name, jersey or position"
                    data-testid="roster-search"
                  />
                </label>
                <label>
                  <span>Status</span>
                  <TkticSelect
                    value={statusFilter}
                    onChange={(next) =>
                      setStatusFilter(next as RosterStatusFilter)
                    }
                    options={[...STATUS_FILTERS]}
                    data-testid="roster-status-filter"
                    ariaLabel="Filter players by status"
                    size="sm"
                  />
                </label>
                <label>
                  <span>Account link</span>
                  <TkticSelect
                    value={accountLinkFilter}
                    onChange={(next) =>
                      setAccountLinkFilter(next as PlayerAccountLinkFilter)
                    }
                    options={[...ACCOUNT_LINK_FILTERS]}
                    data-testid="roster-account-link-filter"
                    ariaLabel="Filter players by account link"
                    size="sm"
                  />
                </label>
                {canUseFeedbackSurfaces ? (
                  <label className="player-queue-attention">
                    <span>Coach attention</span>
                    <TkticSelect
                      value={attentionFilter}
                      onChange={(next) =>
                        setAttentionFilter(next as PlayerAttentionFilter)
                      }
                      options={[...ATTENTION_FILTERS]}
                      data-testid="player-attention-filter"
                      ariaLabel="Filter players by coach attention"
                      size="sm"
                    />
                  </label>
                ) : null}
              </div>
            ) : null}

            <div className="player-queue-rows">
              {filtered.length === 0 ? (
                <p
                  className="text-ink-muted text-sm px-3 py-6"
                  data-testid="roster-no-match"
                >
                  No players match your filters.{" "}
                  <button
                    type="button"
                    onClick={() => {
                      setSearch("");
                      setStatusFilter("all");
                      setAccountLinkFilter("all");
                      setAttentionFilter("all");
                    }}
                    className="text-accent hover:underline"
                  >
                    Clear filters
                  </button>
                </p>
              ) : (
                filtered.map((player) => (
                  <PlayerQueueItem
                    key={player.id}
                    player={player}
                    selected={selectedPlayer?.id === player.id}
                    status={
                      canUseFeedbackSurfaces
                        ? buildPlayerFeedbackStatus(player, notes, clips, goals)
                        : buildPlayerRosterStatus(player)
                    }
                    onSelect={() => selectPlayer(player.id)}
                  />
                ))
              )}
            </div>
            <div className="px-3 pb-3">
              <CollectionPagination collection={collection} />
            </div>
          </section>

          {filtered.length > 0 && selectedPlayer ? (
            <PlayerFeedbackHome
              canUseFeedbackSurfaces={canUseFeedbackSurfaces}
              player={selectedPlayer}
              notes={notes}
              clips={clips}
              goals={goals}
              arrivalContext={routeSearch.context}
              deleting={deleting === selectedPlayer.id}
              canManageRoster={canManageRoster}
              mutations={mutations}
              onGiveFeedback={() => onGiveFeedback(selectedPlayer.id)}
              onAddObservation={() => handleAddObservation(selectedPlayer.id)}
              onAddNoteClip={() => handleAddNoteClip(selectedPlayer.id)}
              onOpenDevelopment={() =>
                onOpenDevelopment(
                  selectedPlayer.id,
                  selectedPlayer.display_name,
                )
              }
              onCreateGoal={
                canUseFeedbackSurfaces && onCreatePlayerGoal
                  ? () => onCreatePlayerGoal(selectedPlayer.id)
                  : undefined
              }
              onOpenGoal={onOpenPlayerGoal}
              onLink={() => setLinkModal(selectedPlayer)}
              onUnlink={async (linkId) => {
                try {
                  await mutations.unlinkAccount(linkId);
                  toast.success("Account unlinked");
                } catch (err) {
                  toast.error("Couldn't unlink account", {
                    description: errorMessage(err),
                  });
                }
              }}
              onEdit={() => setEditModal(selectedPlayer)}
              onDelete={() => setConfirmDelete(selectedPlayer)}
            />
          ) : null}
        </div>
      )}

      {addPlayerOpen ? (
        <AddPlayerModal
          mutations={mutations}
          onCreated={(player) => {
            setAddPlayerOpen(false);
            setSearch("");
            setStatusFilter("all");
            setAttentionFilter("all");
            setRevealPlayerId(player.id);
            selectPlayer(player.id);
          }}
          onClose={() => setAddPlayerOpen(false)}
        />
      ) : null}
      {editModal ? (
        <PlayerModal
          player={editModal}
          mutations={mutations}
          onClose={() => setEditModal(null)}
        />
      ) : null}
      {linkModal ? (
        <LinkAccountModal
          player={linkModal}
          mutations={mutations}
          onClose={() => setLinkModal(null)}
        />
      ) : null}
      {importOpen ? (
        <RosterImportModal onClose={() => setImportOpen(false)} />
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Player Feedback Home — Phase 2 player-first roster replacement
// ---------------------------------------------------------------------------

type PlayerFeedbackStatus = {
  lastFeedbackAt: string | null;
  lastFeedbackLabel: string;
  needsFollowUp: boolean;
  needsHelp: number;
  triedThis: number;
  noRecentFeedback: boolean;
  noFamilyLink: boolean;
  activeGoals: number;
  staleGoals: number;
  nextAction: string;
  nextActionDetail: string;
};

function PlayerQueueItem({
  player,
  selected,
  status,
  onSelect,
}: {
  player: Player;
  selected: boolean;
  status: PlayerFeedbackStatus;
  onSelect: () => void;
}) {
  const queueMeta = [
    player.jersey_number.trim() ? `#${player.jersey_number.trim()}` : "",
    player.position?.trim() ?? "",
  ].filter(Boolean);

  return (
    <button
      type="button"
      onClick={onSelect}
      className={[
        "rounded border px-3 py-2.5 text-left transition-colors",
        selected
          ? "border-accent bg-accent/10"
          : "border-transparent hover:border-rule hover:bg-field",
      ].join(" ")}
      aria-pressed={selected}
      data-testid="roster-row"
      data-player-id={player.id}
    >
      <div className="flex items-start gap-3">
        <PlayerAvatar
          player={player}
          size="sm"
          className={selected ? "border-accent ring-2 ring-accent/30" : ""}
        />
        <span className="min-w-0 flex-1">
          <span className="block truncate font-display text-sm text-ink">
            {player.display_name}
          </span>
          {queueMeta.length > 0 ? (
            <span
              className={[
                "mt-1 block truncate text-xs",
                selected ? "text-ink" : "text-ink-muted",
              ].join(" ")}
              data-testid="roster-row-position"
            >
              {queueMeta.join(" · ")}
            </span>
          ) : null}
        </span>
      </div>
      <div className="player-queue-status">
        {player.consent_on_file === false ? (
          // The operational gate before uploading. `stale` and `missing`
          // need different actions — chase a re-affirmation vs collect
          // consent for the first time — and a DOC_VERSION bump flips
          // every roster to `stale` at once, so one shared label would
          // make the pill unreadable exactly when it matters most.
          <span
            data-testid="roster-row-no-consent"
            data-consent-status={player.consent_status ?? "missing"}
          >
            <AttentionPill
              tone="flag"
              label={
                player.consent_status === "withdrawn"
                  ? "Withdrawn"
                  : player.consent_status === "stale"
                    ? "Re-consent"
                    : "No consent"
              }
            />
          </span>
        ) : null}
        <Badge variant={status.noFamilyLink ? "flag" : "muted"}>
          {status.noFamilyLink ? "No link" : "Linked"}
        </Badge>
      </div>
    </button>
  );
}

function formatConsentDate(iso: string): string {
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return iso;
  return parsed.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function PlayerFeedbackHome({
  canUseFeedbackSurfaces,
  canManageRoster,
  player,
  notes,
  clips,
  goals,
  arrivalContext,
  deleting,
  mutations,
  onGiveFeedback,
  onAddObservation,
  onAddNoteClip,
  onOpenDevelopment,
  onCreateGoal,
  onOpenGoal,
  onLink,
  onUnlink,
  onEdit,
  onDelete,
}: RosterRowProps & {
  canUseFeedbackSurfaces: boolean;
  canManageRoster: boolean;
  mutations: ReturnType<typeof useRosterMutations>;
  notes: CoachingNote[];
  clips: CoachingClip[];
  goals: PlayerGoal[];
  arrivalContext?: PlayerArrivalContext;
  onGiveFeedback: () => void;
}) {
  const status = buildPlayerFeedbackStatus(player, notes, clips, goals);
  const arrivalCopy = arrivalContext
    ? playerArrivalContextCopy(arrivalContext)
    : null;
  const playerGoals = goalsForPlayer(goals, player.id);
  const navigate = useNavigate();
  const feedback = useCollection(feedbackForPlayer(notes, clips, player.id), {
    scope: `player-feedback:${player.id}`,
    text: (item) => `${item.title} ${item.detail} ${item.kind}`,
    matches: (item, filter) => item.kind === filter,
  });
  const goalRows = useCollection(playerGoals, {
    scope: `player-goals:${player.id}`,
    text: (goal) => `${goal.title} ${goal.description ?? ""}`,
    matches: (goal, filter) => goal.status === filter,
  });

  return (
    <section
      className="coach-player-home"
      data-testid="player-feedback-home"
      data-player-id={player.id}
    >
      <header className="player-home-heading">
        <div className="player-home-identity">
          <PlayerAvatar player={player} size="lg" />
          <div>
            <span className="eyebrow">
              {[
                player.position?.trim(),
                player.jersey_number.trim()
                  ? `#${player.jersey_number.trim()}`
                  : "",
              ]
                .filter(Boolean)
                .join(" · ")}
            </span>
            <h2>{player.display_name}</h2>
            <Badge variant={player.active ? "good" : "muted"}>
              {player.active ? "Active" : "Inactive"}
            </Badge>
          </div>
          {player.notes?.trim() ? (
            <p
              className="text-sm text-ink-muted"
              data-testid="player-card-notes"
            >
              {player.notes.trim()}
            </p>
          ) : null}
        </div>
        {canUseFeedbackSurfaces ? (
          <div className="player-home-actions">
            <Button onClick={onGiveFeedback}>Give feedback</Button>
            {onCreateGoal ? (
              <Button
                onClick={onCreateGoal}
                variant="outline"
                data-testid="roster-action-goal"
              >
                Create goal
              </Button>
            ) : null}
          </div>
        ) : null}
      </header>
      {arrivalCopy && canUseFeedbackSurfaces ? (
        <section
          className="player-arrival-context"
          data-testid="player-arrival-context"
        >
          <h3 className="text-ink">{arrivalCopy.title}</h3>
          <p>{arrivalCopy.detail}</p>
        </section>
      ) : null}
      {canUseFeedbackSurfaces ? (
        <section className="player-next-action">
          <span className="eyebrow">Next coach action</span>
          <h3>{status.nextAction}</h3>
          <p>{status.nextActionDetail}</p>
          <div className="player-home-actions">
            <Button
              onClick={onOpenDevelopment}
              variant="outline"
              data-testid="roster-action-development"
            >
              Open plan
            </Button>
            <Button
              onClick={onAddObservation}
              variant="ghost"
              data-testid="roster-action-observation"
            >
              Add observation
            </Button>
            {onAddNoteClip ? (
              <Button onClick={onAddNoteClip} variant="ghost">
                Add video note / clip
              </Button>
            ) : null}
          </div>
        </section>
      ) : (
        <section className="player-next-action">
          <h3>Roster record</h3>
          <p>
            Manage this player's roster details, photo, and account links
            without opening coaching-feedback tools.
          </p>
          {player.notes?.trim() ? (
            <p data-testid="player-roster-notes">{player.notes.trim()}</p>
          ) : null}
        </section>
      )}
      <section className="player-home-divided player-consent">
        <div className="flex items-center justify-between gap-3">
          <h3>Consent</h3>
          <Badge
            variant={player.consent_status === "current" ? "good" : "flag"}
          >
            {player.consent_status === "current"
              ? "On file"
              : player.consent_status === "withdrawn"
                ? "Withdrawn"
                : player.consent_status === "stale"
                  ? "Re-consent"
                  : "No consent"}
          </Badge>
        </div>
        <p
          className="text-sm text-ink-muted"
          data-testid="player-consent-detail"
          data-status={player.consent_status ?? "missing"}
        >
          {player.consent_status === "withdrawn"
            ? "Consent WITHDRAWN by the family — do not upload new video or feedback for this player."
            : player.consent
              ? `${player.consent_status === "current" ? "Consent on file" : "Consented under an older document version — needs re-consent"} · ${formatConsentDate(player.consent.created_at)} · v ${player.consent.version} · ${player.consent.source === "email" ? "recorded by the club" : "affirmed in the app"}`
              : "No consent on file — collect it before uploading new video or feedback."}
        </p>
      </section>
      {canUseFeedbackSurfaces ? (
        <div className="player-feedback-columns">
          <section className="player-home-divided">
            <h3>Feedback history</h3>
            <div className="player-response-context">
              <RosterResponseChips
                summary={player.signal_summary}
                onOpenDevelopment={onOpenDevelopment}
              />
              <p className="text-xs text-ink-muted">
                Last feedback: {status.lastFeedbackLabel}
              </p>
            </div>
            <CollectionToolbar
              collection={feedback}
              label="feedback items"
              filterLabel="Kind"
              filters={[
                { value: "", label: "All kinds" },
                { value: "note", label: "Note" },
                { value: "observation", label: "Observation" },
                { value: "clip", label: "Clip" },
              ]}
            />
            <div className="player-feedback-history">
              {feedback.items.map((item) => (
                <div
                  key={`${item.kind}-${item.id}`}
                  className="player-feedback-history-row"
                >
                  <div>
                    <strong>{item.title}</strong>
                    <p className="text-sm text-ink-muted">
                      {item.kind} · {formatShortDate(item.at)} · {item.detail}
                    </p>
                  </div>
                  <Button
                    variant="ghost"
                    onClick={() =>
                      void navigate({
                        to: "/coach/review",
                        search:
                          item.kind === "clip"
                            ? { clip_id: item.id }
                            : { note_id: item.id },
                      })
                    }
                  >
                    Open
                  </Button>
                </div>
              ))}
              {feedback.items.length === 0 ? (
                <p className="text-sm text-ink-muted">No matching feedback.</p>
              ) : null}
            </div>
            <CollectionPagination
              collection={feedback}
              label="feedback items"
            />
          </section>
          <section className="player-home-divided">
            <h3>Goals</h3>
            <CollectionToolbar
              collection={goalRows}
              label="goals"
              filters={[
                { value: "", label: "All statuses" },
                ...[...new Set(playerGoals.map((goal) => goal.status))].map(
                  (value) => ({ value, label: value.replaceAll("_", " ") }),
                ),
              ]}
            />
            <div className="player-home-goals">
              {goalRows.items.map((goal) => (
                <div
                  key={goal.id}
                  className="player-home-goal"
                  data-testid="player-home-goal"
                  data-goal-id={goal.id}
                >
                  <div>
                    <strong>{goal.title}</strong>
                    <Badge
                      variant={goal.needs_coach_follow_up ? "flag" : "accent"}
                    >
                      {goal.status.replaceAll("_", " ")}
                    </Badge>
                    {isStaleGoal(goal) ? (
                      <AttentionPill tone="flag" label="Goal stale" />
                    ) : null}
                  </div>
                  {onOpenGoal ? (
                    <Button
                      variant="ghost"
                      onClick={() => onOpenGoal(goal)}
                      data-testid="player-home-goal-open"
                    >
                      Open goal
                    </Button>
                  ) : null}
                </div>
              ))}
              {goalRows.items.length === 0 ? (
                <p className="text-sm text-ink-muted">No matching goals.</p>
              ) : null}
            </div>
            <CollectionPagination collection={goalRows} label="goals" />
          </section>
        </div>
      ) : null}
      <section className="player-home-divided player-home-accounts">
        <h3>Accounts</h3>
        <AccountLinks player={player} onUnlink={onUnlink} />
        <div className="player-home-actions">
          <Button
            variant="outline"
            onClick={onLink}
            data-testid="roster-action-link"
          >
            {player.links?.length ? "Manage account link" : "Link account"}
          </Button>
          <Button
            variant="ghost"
            onClick={onEdit}
            data-testid="roster-action-edit"
          >
            Edit player
          </Button>
          {canManageRoster ? (
            <PlayerPhotoControls player={player} mutations={mutations} />
          ) : null}
          <Button
            variant="ghost"
            className="player-delete"
            onClick={onDelete}
            disabled={deleting}
            data-testid="roster-action-delete"
          >
            Delete player
          </Button>
        </div>
      </section>
    </section>
  );
}

function AccountLinks({
  player,
  onUnlink,
}: {
  player: Player;
  onUnlink: (linkId: number) => void;
}) {
  const links = player.links ?? [];
  if (links.length === 0) {
    return (
      <div className="mt-3">
        <p className="text-sm text-ink-muted">
          No family or player account linked.
        </p>
      </div>
    );
  }
  return (
    <div className="mt-3 flex flex-wrap gap-1">
      {links.map((link) => (
        <button
          key={link.id}
          type="button"
          onClick={() => onUnlink(link.id)}
          data-testid="roster-link-chip"
          title={`Unlink @${link.username} - ${link.relationship}`}
          className="inline-flex items-center gap-1 rounded-full border border-rule bg-canvas px-2 py-0.5 text-xs text-ink transition-colors hover:border-destructive hover:text-destructive"
        >
          <span className="text-ink-muted">{link.relationship}</span>
          <span>@{link.username}</span>
          <span aria-hidden="true">x</span>
        </button>
      ))}
    </div>
  );
}

function PlayerPhotoControls({
  player,
  mutations,
}: {
  player: Player;
  mutations: ReturnType<typeof useRosterMutations>;
}) {
  const toast = useToast();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [removePhoto, setRemovePhoto] = useState(false);
  const [avatarStyle, setAvatarStyle] = useState<"neutral" | "female" | "male">(
    player.avatar_style ?? "neutral",
  );
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!selectedFile) {
      setPreviewUrl(null);
      return;
    }
    const url = URL.createObjectURL(selectedFile);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [selectedFile]);

  const beginEditing = () => {
    setAvatarStyle(player.avatar_style ?? "neutral");
    setSelectedFile(null);
    setRemovePhoto(false);
    setOpen(true);
  };

  const save = async () => {
    setBusy(true);
    try {
      if (avatarStyle !== (player.avatar_style ?? "neutral")) {
        await mutations.updatePlayer(player.id, { avatar_style: avatarStyle });
      }
      if (selectedFile) {
        await mutations.uploadPlayerPhoto(player.id, selectedFile);
      } else if (removePhoto && player.photo_filename) {
        await mutations.removePlayerPhoto(player.id);
      }
      toast.success("Player appearance saved");
      setOpen(false);
    } catch (err) {
      toast.error("Couldn't save player appearance", {
        description: errorMessage(err),
      });
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <>
      <Button
        type="button"
        variant="ghost"
        onClick={beginEditing}
        data-testid="player-photo-open"
      >
        Player photo
      </Button>
      {open ? (
        <CoachModal
          title={`Player photo & avatar · ${player.display_name}`}
          onClose={() => setOpen(false)}
          data-testid="player-photo-modal"
          footer={
            <div className="flex flex-wrap justify-end gap-2">
              <Button
                type="button"
                variant="ghost"
                onClick={() => setOpen(false)}
              >
                Cancel
              </Button>
              <Button type="button" onClick={save} disabled={busy}>
                {busy ? "Saving…" : "Save appearance"}
              </Button>
            </div>
          }
        >
          <div className="flex flex-col gap-4">
            <div className="flex items-center gap-3 rounded bg-elevated p-3">
              {previewUrl ? (
                <span className="inline-flex size-14 shrink-0 overflow-hidden rounded border border-rule bg-field">
                  <img
                    src={previewUrl}
                    alt="Selected player preview"
                    className="size-full object-cover"
                  />
                </span>
              ) : (
                <PlayerAvatar
                  player={{
                    ...player,
                    avatar_style: avatarStyle,
                    photo_filename: removePhoto ? null : player.photo_filename,
                  }}
                  size="lg"
                />
              )}
              <div className="min-w-0">
                <strong className="block truncate font-display text-ink">
                  {player.display_name}
                </strong>
                <span className="text-xs text-ink-muted">
                  {[
                    player.position,
                    player.jersey_number ? `#${player.jersey_number}` : "",
                  ]
                    .filter(Boolean)
                    .join(" · ")}
                </span>
              </div>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <section className="flex min-w-0 flex-col gap-3">
                <h3 className="text-base font-display text-ink">
                  Choose a photo
                </h3>
                <input
                  ref={inputRef}
                  type="file"
                  accept=".png,.jpg,.jpeg,.webp,image/png,image/jpeg,image/webp"
                  className="sr-only"
                  onChange={(event) => {
                    const file = event.currentTarget.files?.[0] ?? null;
                    setSelectedFile(file);
                    if (file) setRemovePhoto(false);
                  }}
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => inputRef.current?.click()}
                >
                  <Camera aria-hidden="true" />
                  {selectedFile?.name ??
                    (player.photo_filename ? "Replace photo" : "Choose photo")}
                </Button>
                {(player.photo_filename || selectedFile) && !removePhoto ? (
                  <Button
                    type="button"
                    variant="ghost"
                    className="self-start text-destructive"
                    onClick={() => {
                      setSelectedFile(null);
                      setRemovePhoto(true);
                    }}
                  >
                    Remove photo
                  </Button>
                ) : null}
                <p className="text-xs leading-5 text-ink-muted">
                  PNG, JPEG or WebP. The saved photo appears before the fallback
                  avatar.
                </p>
              </section>

              <section className="flex min-w-0 flex-col gap-3">
                <h3 className="text-base font-display text-ink">
                  Avatar when no photo is available
                </h3>
                <div
                  className="grid grid-cols-3 gap-2"
                  role="group"
                  aria-label="Avatar style"
                >
                  {(
                    [
                      ["neutral", "Neutral"],
                      ["female", "Female"],
                      ["male", "Male"],
                    ] as const
                  ).map(([value, label]) => (
                    <button
                      key={value}
                      type="button"
                      aria-pressed={avatarStyle === value}
                      onClick={() => setAvatarStyle(value)}
                      className="player-avatar-choice flex min-w-0 flex-col items-center gap-2 rounded border border-rule bg-surface p-2 text-xs text-ink hover:bg-field aria-pressed:border-accent"
                    >
                      <PlayerAvatar
                        player={{
                          ...player,
                          photo_filename: null,
                          avatar_style: value,
                        }}
                        size="md"
                      />
                      <span>{label}</span>
                    </button>
                  ))}
                </div>
                <p className="text-xs leading-5 text-ink-muted">
                  The photo appears first. This avatar is the fallback.
                </p>
              </section>
            </div>
          </div>
        </CoachModal>
      ) : null}
    </>
  );
}

function PhotoRequestsPanel({
  requests,
  loading,
  mutations,
}: {
  requests: PlayerPhotoRequest[];
  loading: boolean;
  mutations: ReturnType<typeof useRosterMutations>;
}) {
  const toast = useToast();
  const [busyId, setBusyId] = useState<string | null>(null);

  if (loading || requests.length === 0) return null;

  const approve = async (requestId: string) => {
    setBusyId(requestId);
    try {
      await mutations.approvePhotoRequest(requestId);
      toast.success("Photo request approved");
    } catch (err) {
      toast.error("Couldn't approve photo request", {
        description: errorMessage(err),
      });
    } finally {
      setBusyId(null);
    }
  };

  const reject = async (requestId: string) => {
    setBusyId(requestId);
    try {
      await mutations.rejectPhotoRequest(requestId);
      toast.success("Photo request rejected");
    } catch (err) {
      toast.error("Couldn't reject photo request", {
        description: errorMessage(err),
      });
    } finally {
      setBusyId(null);
    }
  };

  return (
    <section
      className="rounded border border-rule bg-surface p-3 shadow-sm"
      data-testid="player-photo-requests"
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h3 className="text-sm font-display uppercase tracking-wider text-ink-muted">
            Pending photo requests
          </h3>
          <p className="mt-1 text-xs text-ink-muted">
            Review player or family-submitted photo changes before they become
            canonical.
          </p>
        </div>
        <Badge variant="accent">{String(requests.length)}</Badge>
      </div>
      <div className="mt-3 grid gap-2 md:grid-cols-2 xl:grid-cols-3">
        {requests.map((request) => (
          <div
            key={request.id}
            className="flex min-w-0 items-center gap-3 rounded border border-rule bg-canvas p-2"
          >
            <PhotoRequestPreview request={request} />
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-display text-ink">
                {request.player_jersey_number
                  ? `#${request.player_jersey_number} `
                  : ""}
                {request.player_display_name || "Player"}
              </p>
              <p className="truncate text-xs text-ink-muted">
                From{" "}
                {request.requester_display_name ||
                  request.requester_username ||
                  "linked account"}
              </p>
            </div>
            <div className="inline-flex items-center gap-0.5 rounded border border-rule bg-surface p-0.5">
              <IconButton
                label="Approve"
                testid="photo-request-approve"
                onClick={() => approve(request.id)}
                disabled={busyId === request.id}
                icon={<Check size={16} strokeWidth={2} aria-hidden="true" />}
              />
              <IconButton
                label="Reject"
                testid="photo-request-reject"
                onClick={() => reject(request.id)}
                disabled={busyId === request.id}
                danger
                icon={<X size={16} strokeWidth={2} aria-hidden="true" />}
              />
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function PhotoRequestPreview({ request }: { request: PlayerPhotoRequest }) {
  const url = usePlayerPhoto("request", request.id, request.filename);
  return (
    <span className="inline-flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded border border-rule bg-field text-xs font-display text-ink-muted">
      {url ? (
        <img src={url} alt="" className="h-full w-full object-cover" />
      ) : (
        <Camera size={18} strokeWidth={2} aria-hidden="true" />
      )}
    </span>
  );
}

function AttentionPill({
  tone,
  label,
}: {
  tone: "default" | "accent" | "flag" | "good";
  label: string;
}) {
  const variant = {
    default: "muted",
    accent: "accent",
    flag: "flag",
    good: "good",
  }[tone] as "muted" | "accent" | "flag" | "good";
  return <Badge variant={variant}>{label}</Badge>;
}

function playerArrivalContextCopy(context: PlayerArrivalContext): {
  title: string;
  detail: string;
} {
  if (context === "need_help") {
    return {
      title: "Arrived from: need-help response",
      detail:
        "Start with a focused reply or a simpler next step so the feedback lands.",
    };
  }
  if (context === "tried_this") {
    return {
      title: "Arrived from: tried-this response",
      detail:
        "Close the loop by recognizing the attempt and naming the next cue.",
    };
  }
  if (context === "no_recent_feedback") {
    return {
      title: "Arrived from: no recent feedback",
      detail:
        "Create one clear player-facing touch, then attach evidence only if it helps.",
    };
  }
  if (context === "unreviewed_feedback") {
    return {
      title: "Arrived from: shared but not reviewed",
      detail:
        "Check whether the player or family needs a nudge, a simpler summary, or a different evidence package.",
    };
  }
  if (context === "stale_goal") {
    return {
      title: "Arrived from: goal needs attention",
      detail: "Update the plan or create feedback tied to the current goal.",
    };
  }
  return {
    title: "Arrived from: missing recap",
    detail:
      "Use the match context to decide whether this player needs a recap or direct feedback.",
  };
}

function filterPlayerFeedbackHome({
  players,
  notes,
  clips,
  goals,
  filter,
}: {
  players: Player[];
  notes: CoachingNote[];
  clips: CoachingClip[];
  goals: PlayerGoal[];
  filter: PlayerAttentionFilter;
}): Player[] {
  if (filter === "all") return players;
  return players.filter((player) => {
    const status = buildPlayerFeedbackStatus(player, notes, clips, goals);
    if (filter === "needs_follow_up") return status.needsFollowUp;
    if (filter === "need_help") return status.needsHelp > 0;
    if (filter === "no_recent_feedback") return status.noRecentFeedback;
    if (filter === "no_family_link") return status.noFamilyLink;
    return status.staleGoals > 0;
  });
}

function buildPlayerFeedbackStatus(
  player: Player,
  notes: CoachingNote[],
  clips: CoachingClip[],
  goals: PlayerGoal[],
): PlayerFeedbackStatus {
  const feedback = feedbackForPlayer(notes, clips, player.id);
  const playerGoals = goalsForPlayer(goals, player.id);
  const activeGoals = playerGoals.filter(isActiveGoal);
  const staleGoals = activeGoals.filter(isStaleGoal);
  const lastFeedbackAt = feedback[0]?.at ?? null;
  const noRecentFeedback =
    !lastFeedbackAt ||
    Date.now() - Date.parse(lastFeedbackAt) > 21 * 24 * 60 * 60 * 1000;
  const needsHelp = player.signal_summary?.need_help ?? 0;
  const triedThis = player.signal_summary?.tried_this ?? 0;
  const goalFollowUp = activeGoals.some((goal) => goal.needs_coach_follow_up);
  const noFamilyLink = (player.links ?? []).length === 0;

  if (needsHelp > 0) {
    return {
      lastFeedbackAt,
      lastFeedbackLabel: lastFeedbackAt
        ? formatShortDate(lastFeedbackAt)
        : "none yet",
      needsFollowUp: true,
      needsHelp,
      triedThis,
      noRecentFeedback,
      noFamilyLink,
      activeGoals: activeGoals.length,
      staleGoals: staleGoals.length,
      nextAction: "Reply to a need-help response",
      nextActionDetail:
        "A linked player or guardian asked for help. Start by clarifying the cue or giving a smaller next step.",
    };
  }
  if (goalFollowUp || staleGoals.length > 0) {
    return {
      lastFeedbackAt,
      lastFeedbackLabel: lastFeedbackAt
        ? formatShortDate(lastFeedbackAt)
        : "none yet",
      needsFollowUp: true,
      needsHelp,
      triedThis,
      noRecentFeedback,
      noFamilyLink,
      activeGoals: activeGoals.length,
      staleGoals: staleGoals.length,
      nextAction: "Update the player plan",
      nextActionDetail:
        "A goal needs coach attention. Connect the next feedback touch to the current plan.",
    };
  }
  if (triedThis > 0) {
    return {
      lastFeedbackAt,
      lastFeedbackLabel: lastFeedbackAt
        ? formatShortDate(lastFeedbackAt)
        : "none yet",
      needsFollowUp: true,
      needsHelp,
      triedThis,
      noRecentFeedback,
      noFamilyLink,
      activeGoals: activeGoals.length,
      staleGoals: staleGoals.length,
      nextAction: "Close the loop",
      nextActionDetail:
        "The player says they tried the feedback. Reinforce what changed and name the next cue.",
    };
  }
  if (noRecentFeedback) {
    return {
      lastFeedbackAt,
      lastFeedbackLabel: lastFeedbackAt
        ? formatShortDate(lastFeedbackAt)
        : "none yet",
      needsFollowUp: false,
      needsHelp,
      triedThis,
      noRecentFeedback,
      noFamilyLink,
      activeGoals: activeGoals.length,
      staleGoals: staleGoals.length,
      nextAction: "Give a fresh feedback touch",
      nextActionDetail:
        "There is no recent player-facing feedback. Start with one clear message and optional evidence.",
    };
  }
  if (noFamilyLink) {
    return {
      lastFeedbackAt,
      lastFeedbackLabel: lastFeedbackAt
        ? formatShortDate(lastFeedbackAt)
        : "none yet",
      needsFollowUp: false,
      needsHelp,
      triedThis,
      noRecentFeedback,
      noFamilyLink,
      activeGoals: activeGoals.length,
      staleGoals: staleGoals.length,
      nextAction: "Link a family account",
      nextActionDetail:
        "Roster management is secondary here, but linking an account helps player-facing feedback reach the right people.",
    };
  }
  return {
    lastFeedbackAt,
    lastFeedbackLabel: lastFeedbackAt
      ? formatShortDate(lastFeedbackAt)
      : "none yet",
    needsFollowUp: false,
    needsHelp,
    triedThis,
    noRecentFeedback,
    noFamilyLink,
    activeGoals: activeGoals.length,
    staleGoals: staleGoals.length,
    nextAction: "Plan the next useful touch",
    nextActionDetail:
      "Use a note, clip, goal, or match recap when the next coaching moment is ready.",
  };
}

function buildPlayerRosterStatus(player: Player): PlayerFeedbackStatus {
  const noFamilyLink = (player.links ?? []).length === 0;
  return {
    lastFeedbackAt: null,
    lastFeedbackLabel: "not shown",
    needsFollowUp: false,
    needsHelp: 0,
    triedThis: 0,
    noRecentFeedback: false,
    noFamilyLink,
    activeGoals: 0,
    staleGoals: 0,
    nextAction: player.active
      ? "Active roster player"
      : "Inactive roster player",
    nextActionDetail: noFamilyLink
      ? "Roster account links can be managed from this page."
      : "Roster details and account links are up to date.",
  };
}

function feedbackForPlayer(
  notes: CoachingNote[],
  clips: CoachingClip[],
  playerId: string,
) {
  const noteItems = notes
    .filter((note) => note.player_ids?.includes(playerId))
    .map((note) => ({
      // Classify by note_context so the pill matches where the Feedback
      // Library files it: video notes (match-anchored) land under Notes,
      // observations (incl. quick "Give feedback" with no match) under
      // Observations. Classifying by tactical_board_json instead mislabeled
      // a no-board observation as "note", so coaches looked in Notes and
      // couldn't find it.
      kind: note.note_context === "observation" ? "observation" : "note",
      id: note.id,
      title: note.player_summary || note.title || "Coaching note",
      detail: note.what_to_do_next || note.category || note.visibility,
      at: note.updated_at || note.created_at,
    }));
  const clipItems = clips
    .filter((clip) => clip.player_ids?.includes(playerId))
    .map((clip) => ({
      kind: "clip",
      id: clip.id,
      title: clip.title || "Coaching clip",
      detail: clip.description || clip.category || clip.visibility,
      at: clip.updated_at || clip.created_at,
    }));
  return [...noteItems, ...clipItems].sort((a, b) =>
    String(b.at).localeCompare(String(a.at)),
  );
}

function goalsForPlayer(goals: PlayerGoal[], playerId: string) {
  return goals.filter((goal) => goal.player_id === playerId);
}

function isActiveGoal(goal: PlayerGoal) {
  return goal.status !== "achieved" && goal.status !== "archived";
}

function isStaleGoal(goal: PlayerGoal) {
  if (!isActiveGoal(goal) || !goal.target_date) return false;
  const target = Date.parse(goal.target_date);
  return Number.isFinite(target) && target < Date.now();
}

function formatShortDate(value: string) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "unknown";
  return date.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
  });
}

// ---------------------------------------------------------------------------
// Add Player modal
// ---------------------------------------------------------------------------

function AddPlayerModal({
  mutations,
  onCreated,
  onClose,
}: {
  mutations: ReturnType<typeof useRosterMutations>;
  onCreated: (player: Player) => void;
  onClose: () => void;
}) {
  const toast = useToast();
  const [name, setName] = useState("");
  const [jersey, setJersey] = useState("");
  const [position, setPosition] = useState("");
  const [notes, setNotes] = useState("");
  const [active, setActive] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const clean = name.trim();
    if (!clean) {
      setError("Player name is required.");
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      const created = await mutations.createPlayer({
        avatar_style: "neutral",
        display_name: clean,
        jersey_number: jersey.trim(),
        position,
        notes: notes.trim(),
        active,
      });
      toast.success("Player added");
      onCreated(created.player);
    } catch (err) {
      setError(errorMessage(err));
      toast.error("Couldn't add player", { description: errorMessage(err) });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <CoachModal
      title="Add player"
      onClose={onClose}
      data-testid="roster-add-player-modal"
      footer={
        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            form="roster-add-player-form"
            disabled={submitting}
            data-testid="roster-quick-add-submit"
          >
            {submitting ? "Adding…" : "Add player"}
          </Button>
        </div>
      }
    >
      <form
        id="roster-add-player-form"
        onSubmit={handleSubmit}
        className="flex flex-col gap-4"
      >
        <AddPlayerField label="Player name *">
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Full name"
            data-testid="roster-quick-add-name"
            autoFocus
            className="min-h-12 w-full rounded border border-rule bg-field px-3 text-sm focus:outline-none focus:ring-2 focus:ring-accent"
          />
        </AddPlayerField>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <AddPlayerField label="Jersey number">
            <input
              type="text"
              value={jersey}
              onChange={(e) => setJersey(e.target.value)}
              placeholder="Optional"
              data-testid="roster-quick-add-jersey"
              className="min-h-12 w-full rounded border border-rule bg-field px-3 text-sm focus:outline-none focus:ring-2 focus:ring-accent"
            />
          </AddPlayerField>

          <AddPlayerField label="Position">
            <TkticSelect
              value={position}
              onChange={setPosition}
              data-testid="roster-quick-add-position"
              ariaLabel="Position"
              options={PLAYER_POSITION_OPTIONS.map((o) => ({
                value: o.value,
                label: o.label || "Optional",
              }))}
            />
          </AddPlayerField>

          <AddPlayerField label="Status">
            <TkticSelect
              value={active ? "active" : "inactive"}
              onChange={(value) => setActive(value === "active")}
              data-testid="roster-quick-add-status"
              ariaLabel="Status"
              options={[
                { value: "active", label: "Active" },
                { value: "inactive", label: "Inactive" },
              ]}
            />
          </AddPlayerField>
        </div>

        <AddPlayerField label="Roster notes">
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Private roster context for coaches"
            data-testid="roster-quick-add-notes"
            rows={2}
            className="min-h-[4.375rem] w-full resize-y rounded border border-rule bg-field px-3 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-accent"
          />
        </AddPlayerField>

        {error ? (
          <p
            className="text-destructive font-mono text-xs"
            data-testid="roster-quick-add-error"
          >
            {error}
          </p>
        ) : null}
      </form>
    </CoachModal>
  );
}

function AddPlayerField(props: {
  label: string;
  helper?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-xs font-display uppercase tracking-wider text-ink-muted">
        {props.label}
        {props.helper ? (
          <span className="ml-1 normal-case tracking-normal text-ink-muted/70">
            ({props.helper})
          </span>
        ) : null}
      </span>
      {props.children}
    </label>
  );
}

function KpiTile(props: { label: string; value: string }) {
  return (
    <div className="rounded border border-rule bg-surface px-3 py-2 shadow-sm">
      <div className="text-ink-muted text-xs font-display font-semibold uppercase tracking-wider">
        {props.label}
      </div>
      <div className="mt-1 flex items-baseline gap-2">
        <strong className="text-2xl font-display leading-none text-ink tabular-nums">
          {props.value}
        </strong>
      </div>
    </div>
  );
}

type RosterRowProps = {
  player: Player;
  deleting: boolean;
  onAddObservation: () => void;
  /** Routes to Coach Review's video surface (a video note / clip) for this
   * player. Optional: only the Next-coach-action row renders this button, so
   * the secondary RosterActionButtons cluster doesn't need to supply it. */
  onAddNoteClip?: () => void;
  /** Opens the M7 Player Development profile modal for this player. */
  onOpenDevelopment: () => void;
  /** Undefined ⇒ the goal button is hidden (Goals slice wires it). */
  onCreateGoal?: () => void;
  /** Opens a specific existing goal without losing its identity. */
  onOpenGoal?: (goal: PlayerGoal) => void;
  onLink: () => void;
  onUnlink: (linkId: number) => void;
  onEdit: () => void;
  onDelete: () => void;
};

/** Compact square icon button for a roster row action. The text
 * label is exposed via `title` + `aria-label` so the icon-only
 * control stays accessible and discoverable on hover. */
function IconButton(props: {
  label: string;
  testid: string;
  icon: React.ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  danger?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={props.onClick}
      disabled={props.disabled}
      data-testid={props.testid}
      title={props.label}
      aria-label={props.label}
      className={[
        "inline-flex h-7 w-7 shrink-0 items-center justify-center rounded border transition-colors disabled:opacity-40",
        props.danger
          ? "border-rule text-ink-muted hover:border-destructive hover:text-destructive hover:bg-destructive/10"
          : "border-rule text-ink-muted hover:border-accent hover:text-accent hover:bg-accent/10",
      ].join(" ")}
    >
      {props.icon}
    </button>
  );
}

// ---------------------------------------------------------------------------
// PR 8: Roster-row response signal chips
// ---------------------------------------------------------------------------

/** Compact chip strip showing per-player "How'd it land?" reaction
 * counts on the Roster row (PR 8). Mirrors the mockup's Phase 6
 * coach roster (`⚡ 2 ✓ 1 · ? 1 needs help · 1 no response`).
 *
 * When the player has at least one `need_help` reaction, the row
 * surfaces a small orange "Follow up" CTA that opens the Development
 * modal so the coach can see the actual reflection bodies.
 *
 * Hides entirely when no linked account has responded. An em-dash here
 * looked like an empty feedback row and is not part of the approved layout. */
function RosterResponseChips({
  summary,
  onOpenDevelopment,
}: {
  summary?: { got_it: number; need_help: number; tried_this: number };
  onOpenDevelopment: () => void;
}) {
  const got = summary?.got_it ?? 0;
  const need = summary?.need_help ?? 0;
  const tried = summary?.tried_this ?? 0;
  const total = got + need + tried;
  if (total === 0) return null;
  return (
    <div
      className="flex flex-wrap items-center gap-1"
      data-testid="roster-response-chips"
    >
      {got > 0 ? (
        <span
          className="inline-flex items-center gap-0.5 rounded-full bg-good/15 text-good px-1.5 py-0.5 text-[0.65rem] font-mono"
          title="Got it"
          data-signal="got_it"
        >
          <span aria-hidden="true">✓</span>
          {got}
        </span>
      ) : null}
      {tried > 0 ? (
        <span
          className="inline-flex items-center gap-0.5 rounded-full bg-accent/15 text-ink px-1.5 py-0.5 text-[0.65rem] font-mono"
          title="I tried this"
          data-signal="tried_this"
        >
          <span aria-hidden="true">⚡</span>
          {tried}
        </span>
      ) : null}
      {need > 0 ? (
        <button
          type="button"
          onClick={onOpenDevelopment}
          className="inline-flex items-center gap-0.5 rounded-full bg-flag text-navy px-2 py-0.5 text-[0.65rem] font-display uppercase tracking-wider hover:opacity-90 transition-opacity"
          title={`${need} needs follow-up — open the player's plan`}
          data-testid="roster-response-follow-up"
          data-signal="need_help"
        >
          <span aria-hidden="true">?</span>
          {need} follow up
        </button>
      ) : null}
    </div>
  );
}
